import React from 'react';
import PropTypes from 'prop-types';
import {
  fetchJsonWithCredentialsOptions,
  buildQueryString,
} from 'js/fetch-utils';

import { search as trackSearch } from '../../tracking';

const emptySearchResult = {
  results: [],
  totalCount: 0,
  moreLink: null,
};

export default ComposedComponent =>
  class extends React.Component {
    static displayName = 'Search';

    static defaultProps = {
      limit: 10,
      showLoadMore: true,
      showResultHeaders: true,
      showSearchSummary: true,
    };

    static propTypes = {
      classNames: PropTypes.shape({
        search: PropTypes.string,
        results: PropTypes.string,
      }),
      limit: PropTypes.number.isRequired,
      showLoadMore: PropTypes.bool.isRequired,
      showResultHeaders: PropTypes.bool.isRequired,
      showSearchSummary: PropTypes.bool.isRequired,
      language: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      query: PropTypes.string,
    };

    state = {
      employees: emptySearchResult,
      pages: emptySearchResult,
      error: null,
      searching: false,
      submittedSearchQuery: '',
    };

    _search = query => {
      const { language, url, limit } = this.props;

      if (!query || query.length === 0) {
        return this._clearSearch();
      }

      if (!query || query.length < 3) return;

      if (this._searchAbortController) {
        this._searchAbortController.abort();
      }
      this._searchAbortController = new AbortController();
      const { signal } = this._searchAbortController;

      this.setState({ searching: true });

      const params = buildQueryString({ q: query, limit, language });

      Promise.all([
        fetch(`${url}/standard?${params}`, {
          ...fetchJsonWithCredentialsOptions,
          signal,
        }).then(r => r.json()),
        fetch(`${url}/employees?${params}`, {
          ...fetchJsonWithCredentialsOptions,
          signal,
        }).then(r => r.json()),
      ])
        .then(([pagesRes, employeesRes]) => {
          trackSearch(query, pagesRes.totalCount + employeesRes.totalCount);

          this.setState({
            error: false,
            submittedSearchQuery: query,
            searching: false,
            pages: pagesRes,
            employees: employeesRes,
          });
        })
        .catch(error => {
          if (error.name === 'AbortError') return;
          console.error(error);
          this.setState({
            submittedSearchQuery: '',
            searching: false,
            pages: emptySearchResult,
            employees: emptySearchResult,
            error: error.message || error.statusText,
          });
        });
    };

    _clearSearch = () => {
      this.setState({
        employees: {
          ...this.state.employees,
          results: [],
        },
        pages: {
          ...this.state.pages,
          results: [],
        },
      });
    };

    _loadMore = type => {
      let url;
      let node;

      switch (type) {
        case 'pages':
          url = this.state.pages.moreLink;
          node = 'pages';
          break;
        case 'employees':
          url = this.state.employees.moreLink;
          node = 'employees';
          break;
        default:
          console.error('_loadMore: Unknown type', type);
          return;
      }

      if (!url) return;

      fetch(url)
        .then(res => res.json())
        .then(data => {
          this.setState({
            [node]: {
              moreLink: data.moreLink,
              totalCount: data.totalCount,
              results: this.state[node].results.concat(data.results),
            },
          });
        })
        .catch(error => {
          console.error(error);
        });
    };

    render() {
      return (
        <ComposedComponent
          {...this.props}
          {...this.state}
          loadMore={this._loadMore}
          search={this._search}
        />
      );
    }
  };
